import { useState, useContext, useEffect, useRef } from 'react'
import { QuoteContext } from '../context/context'
import moment from 'moment'

// Utility functions
import { printCurrencySymbol } from '../utils/functions'

// Constants
import { BOARD_TYPES, FEE_TYPES, ROOM_TYPES } from '../../../assets/data/exports'

// UI components
import { CaretDownIcon, CaretUpIcon } from '../../../utils/svgs'
import { ArrowRightIcon, MinusIcon, PlusIcon, WarningTriangleIcon } from '../../../utils/svgs-v2'
import CalendarUI from '../components/calendar/calendar'
import Input from '../../../components/ui/inputs/input'
import Badge from '../../../components/ui/badge/badge'
import Button from '../../../components/ui/button/button'
import Select from '../../../components/ui/select/select'
import Checkbox from '../../../components/ui/checkbox/checkbox'

// Returns the markup for a resort now
export default function OptionPackages({ details }) {
    const [open, setOpen] = useState(true)
    const [choosingNightsID, setChoosingNightsID] = useState('')
    const [choosingNights, setChoosingNights] = useState('')
    const [choosingNightsPackage, setChoosingNightsPackage] = useState({})

    // Calendar dates
    const [dateOverrun, setDateOverrun] = useState(false)
    const [chosenStartDate, setChosenStartDate] = useState(0)
    const [chosenDates, setChosenDates] = useState([])

    // Packages states
    const [windowPackages, setWindowPackages] = useState([])

    // State for the custom pricing options
    const [custNights, setCustNights] = useState(null)
    const [custRounds, setCustRounds] = useState(null)
    const [custRoomType, setCustRoomType] = useState(null)
    const [custBoardType, setCustBoardType] = useState(null)
    const [custFeeType, setCustFeeType] = useState(null)
    const [custNetCost, setCustNetCost] = useState(null)

    // When auto-pick lands on a non-matching window (closest nights / no room
    // match), flag it so the agent knows the seeded inputs weren't an exact fit.
    const [autoPickMismatch, setAutoPickMismatch] = useState('')

    // Room-type-first auto-pick (seeded build tab, resorts only). Before picking
    // a package we ask the agent which room types suit the group, then only
    // consider packages that actually offer one of them.
    const [roomTypeChoices, setRoomTypeChoices] = useState({})
    const [selectedRoomTypes, setSelectedRoomTypes] = useState([])
    const [awaitingRoomChoice, setAwaitingRoomChoice] = useState(false)

    // Get the selected packages and the update method from the quote context
    const { ctxSelectedPackages, setCtxSelectedPackages, ctxIncludedDates, ctxEnquirySeed } = useContext(QuoteContext)

    // Only run the enquiry auto-pick once per mounted option, otherwise removing
    // an auto-picked package would immediately re-add it.
    const autoPickedRef = useRef(false)

    // On component mount
    useEffect(() => {
        // Reset the state
        setChosenDates([])
        setChosenStartDate(0)

        // Are there any dates selected in the context?
        if (ctxIncludedDates && ctxIncludedDates.length > 0) {
            // Set the dates back locally here
            setChosenDates(ctxIncludedDates)

            // Recalibrate the chosen start date in the state
            handleDateChosen(moment(ctxIncludedDates[0], 'DD/MM/YYYY'), true)
        }
    }, [])

    // Enquiry auto-seed. Only runs on the seeded Build quote tab (ctxEnquirySeed
    // present); the standalone quoting page mounts without a seed so this is a
    // complete no-op there. For resorts we first ask the agent which room types
    // suit the group, then auto-pick a package offering one of them; courses
    // (no room types) auto-pick straight away.
    useEffect(() => {
        if (!ctxEnquirySeed || autoPickedRef.current) return

        const checkInMillis = ctxEnquirySeed.checkInMillis || (ctxEnquirySeed.checkInSeconds ? ctxEnquirySeed.checkInSeconds * 1000 : 0)
        if (!checkInMillis) return

        // Don't auto-pick if this option already has a package selected
        const alreadySelected = ctxSelectedPackages.some((pkg) => (
            pkg.resort_id === details.objectID || pkg.course_id === details.objectID
        ))
        if (alreadySelected) return

        // Courses have no room types - auto-pick the cheapest spanning window now.
        if (details.option_type === 'course') {
            autoPickedRef.current = true
            runAutoPick(checkInMillis, [])
            return
        }

        // Resorts: gather every room type the hotel offers across its (non-draft)
        // windows and ask the agent which they want before picking. If the hotel
        // lists no room types at all, auto-pick on date/nights with no room filter.
        const offered = {}
        ;(details?.price_windows?.resort || []).forEach((window) => {
            if (window.is_draft) return
            ;(window.room_types || []).forEach((room) => {
                if (room.type && ROOM_TYPES[room.type]) offered[room.type] = ROOM_TYPES[room.type]
            })
        })

        if (Object.keys(offered).length === 0) {
            autoPickedRef.current = true
            runAutoPick(checkInMillis, [])
            return
        }

        // Collapse the calendar/packages while the agent picks room types so the
        // chooser is the focus; "pick manually" re-opens it. Start with nothing
        // ticked so the agent consciously picks the rooms that suit the group.
        setRoomTypeChoices(offered)
        setAwaitingRoomChoice(true)
        setOpen(false)
    }, [])

    // True when this option's For PAX is unset or the seeded group size is a
    // multiple of it (the CMS rule: only bookable in multiples of For PAX).
    const paxCompatible = (windowPackage) => {
        const forPax = parseInt(windowPackage.for_pax)
        if (isNaN(forPax) || forPax <= 0) return true
        const groupSize = parseInt(ctxEnquirySeed?.groupSize)
        return !isNaN(groupSize) && groupSize % forPax === 0
    }

    // Cheapest window by its lowest base price.
    const cheapestWindow = (windows) => windows.reduce((cheapest, current) => (
        (parseFloat(current.price_lowest) || Infinity) < (parseFloat(cheapest.price_lowest) || Infinity) ? current : cheapest
    ))

    // Does this window offer at least one of the agent's chosen room types?
    const windowOffersRoom = (windowPackage, roomTypes) => (
        (windowPackage.room_types || []).some((room) => roomTypes.includes(room.type))
    )

    // The cheapest of the agent's chosen room types this window offers - the key
    // we pre-select in the package config.
    const bestRoomKey = (windowPackage, roomTypes) => {
        const matching = (windowPackage.room_types || []).filter((room) => roomTypes.includes(room.type))
        if (matching.length === 0) return null
        return matching.reduce((best, room) => (
            (parseFloat(room.cost) || Infinity) < (parseFloat(best.cost) || Infinity) ? room : best
        )).type
    }

    // Run the enquiry auto-pick for this option against the seeded check-in and
    // the agent's chosen room types (empty = no room filter, e.g. courses).
    const runAutoPick = (checkInMillis, roomTypes) => {
        // Build a moment for the seeded check-in and drive the date handler so
        // matching windows load + the calendar reflects the date
        const checkInMoment = moment(checkInMillis)
        handleDateChosen(checkInMoment.clone())

        const startSeconds = parseInt(checkInMoment.clone().startOf('day').format('X'))
        const allWindows = details.option_type === 'resort'
            ? (details?.price_windows?.resort || [])
            : (details?.price_windows?.course || [])
        const spanning = allWindows.filter((window) => (
            !window.is_draft
            && window.start_date?.seconds <= startSeconds
            && window.end_date?.seconds >= startSeconds
        ))

        // No window spans the check-in -> pre-fill the ad-hoc custom pricing entry
        if (spanning.length === 0) {
            prefillCustomPricing(checkInMoment.clone())
            return
        }

        // Courses: cheapest spanning window (no rooms/nights/pax to weigh).
        if (details.option_type === 'course') {
            addPackageFromWindow(cheapestWindow(spanning), checkInMoment.clone())
            return
        }

        // --- Resort auto-pick ---

        // For PAX guard: drop packages whose For PAX doesn't divide the group, but
        // only when that leaves something to pick (never strand the agent).
        const paxOk = spanning.filter(paxCompatible)
        const pool = paxOk.length > 0 ? paxOk : spanning

        // Room filter: keep packages offering one of the chosen rooms. If none on
        // this date offer them, fall back to the full pool and flag it.
        let roomFiltered = pool
        let roomMismatch = false
        if (roomTypes.length > 0) {
            const offered = pool.filter((window) => windowOffersRoom(window, roomTypes))
            if (offered.length > 0) {
                roomFiltered = offered
            } else {
                roomMismatch = true
            }
        }

        const seedNights = parseInt(ctxEnquirySeed.nights)
        const seedRounds = parseInt(ctxEnquirySeed.rounds)
        const preDefined = roomFiltered.filter((window) => window.type === 'pre_defined')
        const perNight = roomFiltered.filter((window) => window.type === 'per_night')

        let chosen = null
        let nightsOverride = null
        const notices = []

        // 1. Prefer a pre_defined window whose nights match (tie-break on rounds)
        if (!isNaN(seedNights)) {
            const nightsMatches = preDefined.filter((window) => parseInt(window.nights) === seedNights)
            if (nightsMatches.length > 0) {
                const roundsMatches = !isNaN(seedRounds)
                    ? nightsMatches.filter((window) => parseInt(window.rounds) === seedRounds)
                    : []
                chosen = roundsMatches.length > 0 ? cheapestWindow(roundsMatches) : cheapestWindow(nightsMatches)
            }
        }

        // 2. Else a per_night window, seeding nights = enquiry nights
        if (!chosen && perNight.length > 0 && !isNaN(seedNights)) {
            chosen = cheapestWindow(perNight)
            nightsOverride = seedNights
        }

        // 3. Else the closest-nights pre_defined window, flagged
        if (!chosen && preDefined.length > 0) {
            chosen = !isNaN(seedNights)
                ? preDefined.reduce((best, current) => (
                    Math.abs(parseInt(current.nights) - seedNights) < Math.abs(parseInt(best.nights) - seedNights) ? current : best
                ))
                : cheapestWindow(preDefined)

            if (!isNaN(seedNights) && parseInt(chosen.nights) !== seedNights) {
                notices.push(`No ${seedNights}-night package on this date - picked the closest (${chosen.nights} nights).`)
            }
        }

        // 4. Only per_night windows and no seeded nights -> open the row so the
        // agent can enter the nights against the per_night window manually
        if (!chosen && perNight.length > 0) {
            setAwaitingRoomChoice(false)
            setOpen(true)
            saveSelectedPackage(cheapestWindow(perNight))
            return
        }

        // Nothing usable -> ad-hoc custom pricing
        if (!chosen) {
            prefillCustomPricing(checkInMoment.clone())
            return
        }

        if (roomMismatch) {
            notices.push('No package with your chosen room type on this date - review the room type below.')
        }

        if (notices.length > 0) {
            setAutoPickMismatch(notices.join(' '))
        }

        // Pre-select the cheapest matching room type (unless we had to ignore the
        // room filter, in which case leave the room for the agent to choose).
        const roomKey = !roomMismatch ? bestRoomKey(chosen, roomTypes) : null
        addPackageFromWindow(chosen, checkInMoment.clone(), nightsOverride, roomKey)
    }

    // Toggle a room type in the up-front chooser (multi-select).
    const toggleRoomType = (roomKey) => {
        setSelectedRoomTypes((current) => (
            current.includes(roomKey) ? current.filter((key) => key !== roomKey) : [...current, roomKey]
        ))
    }

    // Confirm the chosen room types and run the auto-pick.
    const confirmRoomChoice = () => {
        const checkInMillis = ctxEnquirySeed.checkInMillis || (ctxEnquirySeed.checkInSeconds ? ctxEnquirySeed.checkInSeconds * 1000 : 0)
        autoPickedRef.current = true
        setAwaitingRoomChoice(false)
        runAutoPick(checkInMillis, selectedRoomTypes)
    }

    // When the date is updated from the calendar UI
    const handleDateChosen = (chosenDate, fromState = false) => {
        // If we are not loading these dates back from the state
        if (!fromState) {
            // Push the new start date into the state
            setChosenDates(chosenDate.format('DD-MM-YYYY'))
        }

        // Set the new chosen start date into the state
        setChosenStartDate(chosenDate)

        // Get the start of the day in seconds
        const startSeconds = parseInt(chosenDate.startOf('day').format('X'))

        // Are we working with a resort option type?
        if (details.option_type === 'resort') {
            // Find all the price windows where this day lands inside of
            const matchingResortWindows = details?.price_windows?.resort.filter((window) => {
                return (window.start_date.seconds <= startSeconds) && (window.end_date.seconds >= startSeconds)
            })
    
            // Set these matching windows into the state
            setWindowPackages([...matchingResortWindows])
        } else if (details.option_type === 'course') {
            // Otherwise if this is a course type we are working with
            const matchingCourseWindows = details?.price_windows?.course.filter((window) => {
                return (window.start_date.seconds <= startSeconds) && (window.end_date.seconds >= startSeconds)
            })
    
            // Set these matching windows into the state
            setWindowPackages([...matchingCourseWindows])
        }
    }

    // Take in comenights values and calculate the included dates with the chosen start date
    const calculateIncludedDates = (nights, startMoment = chosenStartDate) => {
        // Setup an array to store the chosen dates
        let chosenDatesLocal = []

        // Get a copy of the chosen start date moment object
        const calculatedStartDate = startMoment.clone()

        // Add the nights to the current chosen start date to see if it is within the bounds
        const currentEndDate = calculatedStartDate.add(parseInt(nights), 'days').startOf('day').valueOf()
        const loopEndDate = calculatedStartDate.startOf('day').valueOf()

        // Get a copy of the chosen start date to use in loopin through the chosen dates
        const loopDate = startMoment.clone()

        // Loop over the two dates to generate an array of chosen dates
        while (loopDate.isBefore(currentEndDate) || loopDate.isSame(loopEndDate)) {
            // Push the parsed date into the array to highlight on the calendar
            chosenDatesLocal.push(loopDate.format('DD-MM-YYYY'))

            // Then add one to the looping date to increment through
            loopDate.add(1, 'days')
        }

        // Return the array of dates
        return chosenDatesLocal
    }

    // Add the chosen package to the basket in the state
    const saveSelectedPackage = (newPackage) => {
        // If the package chosen is one setup 'per night'
        if (newPackage.type === 'per_night') {
            // Set the package ID into the state to toggle the nights window
            setChoosingNightsID(newPackage.id)
            setChoosingNightsPackage(newPackage)
            return
        }
        
        // Fetch the included dates
        const includedDates = calculateIncludedDates(newPackage.nights)

        // Otherwise, push the new package into the quote context
        setCtxSelectedPackages((ctxSelectedPackages) => ([...ctxSelectedPackages, {
            ...newPackage,
            from_date: chosenStartDate.startOf('day').format('X'),
            included_dates: includedDates,
            parent_type: details.option_type,
            parent_title: details.title,
            parent_currency: printCurrencySymbol(details.currency)
        }]))

        // Reset the state
        setChosenDates([])
    }

    // Push a window package straight into the quote context using an explicit
    // start moment (used by the enquiry auto-pick, where we can't rely on the
    // freshly-set chosenStartDate/chosenDates state being available yet). Mirrors
    // the package shape produced by saveSelectedPackage/completeSelectedPackage.
    const addPackageFromWindow = (windowPackage, startMoment, nightsOverride = null, autoRoomType = null) => {
        // Decide how many nights this package spans (per-night windows use the
        // seeded nights, everything else uses the window's own nights value).
        const packageNights = nightsOverride !== null ? parseInt(nightsOverride) : windowPackage.nights

        // Work out the included dates from the explicit start moment
        const includedDates = calculateIncludedDates(packageNights, startMoment)

        // Build the package object, seeding nights for per-night windows
        const packageToAdd = {
            ...windowPackage,
            from_date: startMoment.clone().startOf('day').format('X'),
            included_dates: includedDates,
            parent_type: details.option_type,
            parent_title: details.title,
            parent_currency: printCurrencySymbol(details.currency)
        }

        // Per-night windows carry the seeded nights onto the package
        if (windowPackage.type === 'per_night' && nightsOverride !== null) {
            packageToAdd.nights = parseInt(nightsOverride)
        }

        // Pre-select the agent's chosen room type so the package prices on it
        if (autoRoomType) {
            packageToAdd.auto_room_type = autoRoomType
        }

        // Push the package into the quote context
        setCtxSelectedPackages((ctxSelectedPackages) => ([...ctxSelectedPackages, packageToAdd]))

        // Highlight the spanned dates on the calendar
        setChosenDates(includedDates)

        // On the seeded build tab, collapse the calendar/package picker once
        // we've auto-picked so the agent isn't left scrolling past it to reach
        // the configured package + totals. They can expand the row to change
        // the date or package.
        if (ctxEnquirySeed) {
            setOpen(false)
        }
    }

    // When the amount of nights changes
    useEffect(() => {
        // Do we have a start date chosen?
        if (chosenStartDate && moment(chosenStartDate).isValid()) {
            // Setup an array to store the chosen dates
            let chosenDatesLocal = []

            // Are there any nights available in the state?
            if (choosingNights && !isNaN(parseInt(choosingNights)) && parseInt(choosingNights) > 0) {
                // When does the currently chosen packge due to end?
                const packageEnding = moment(choosingNightsPackage.end_date.seconds, 'X').startOf('day').valueOf()
                
                // Get a copy of the chosen start date moment object
                const calculatedStartDate = chosenStartDate.clone()

                // Add the nights to the current chosen start date to see if it is within the bounds
                const currentEndDate = calculatedStartDate.add(parseInt(choosingNights), 'days').startOf('day').valueOf()
                const loopEndDate = calculatedStartDate.startOf('day').valueOf()

                // Get a copy of the chosen start date to use in loopin through the chosen dates
                const loopDate = chosenStartDate.clone()

                // Loop over the two dates to generate an array of chosen dates
                while (loopDate.isBefore(currentEndDate) || loopDate.isSame(loopEndDate)) {
                    // Push the parsed date into the array to highlight on the calendar
                    chosenDatesLocal.push(loopDate.format('DD-MM-YYYY'))

                    // Then add one to the looping date to increment through
                    loopDate.add(1, 'days')
                }

                // Are we within the bounds of this price window?
                if (currentEndDate > packageEnding) {
                    // Set the state to disable the button
                    setDateOverrun(true)
                } else {
                    // Otherwise reset the state
                    setDateOverrun(false)
                }
            }

            // Then set these dates into the state
            setChosenDates(chosenDatesLocal)
        }
    }, [choosingNights])

    // When the amount of nights changes
    useEffect(() => {
        // Do we have a start date chosen?
        if (chosenStartDate && moment(chosenStartDate).isValid()) {
            // Setup an array to store the chosen dates
            let chosenDatesLocal = []

            // Are there any nights available in the state?
            if (custNights && !isNaN(parseInt(custNights)) && parseInt(custNights) > 0) {
                // Get a copy of the chosen start date moment object
                const calculatedStartDate = chosenStartDate.clone()

                // Add the nights to the current chosen start date to see if it is within the bounds
                const currentEndDate = calculatedStartDate.add(parseInt(custNights), 'days').startOf('day').valueOf()
                const loopEndDate = calculatedStartDate.startOf('day').valueOf()

                // Get a copy of the chosen start date to use in loopin through the chosen dates
                const loopDate = chosenStartDate.clone()

                // Loop over the two dates to generate an array of chosen dates
                while (loopDate.isBefore(currentEndDate) || loopDate.isSame(loopEndDate)) {
                    // Push the parsed date into the array to highlight on the calendar
                    chosenDatesLocal.push(loopDate.format('DD-MM-YYYY'))

                    // Then add one to the looping date to increment through
                    loopDate.add(1, 'days')
                }
            }
            
            // Then set these dates into the state
            setChosenDates(chosenDatesLocal)
        }
    }, [custNights])

    // Complete the selction of a 'per night' package
    const completeSelectedPackage = () => {
        // Firstly let's clear the state
        setChoosingNightsID(null)

        // Get a copy of the new package from the state
        const newPackageNights = choosingNights
        const newPackage = choosingNightsPackage

        // Otherwise, push the new package into the quote context
        setCtxSelectedPackages((ctxSelectedPackages) => ([...ctxSelectedPackages, {
            ...newPackage,
            from_date: chosenStartDate.startOf('day').format('X'),
            nights: parseInt(newPackageNights),
            included_dates: chosenDates,
            parent_type: details.option_type,
            parent_title: details.title,
            parent_currency: printCurrencySymbol(details.currency)
        }]))

        // Then clear the state
        setChoosingNights('')
        setChoosingNightsPackage({})
        setChosenDates([])
    }

    // Remove the chosen package from the basket in the state
    const removeSelectedPackage = (existingPackage) => {
        setCtxSelectedPackages((ctxSelectedPackages) => ctxSelectedPackages.filter((selectedPackage) => (
            selectedPackage.id !== existingPackage.id
        )))

        // Clear the chosen dates
        setChosenDates([])
    }

    // Pre-fill the ad-hoc custom pricing entry from the enquiry seed (used by
    // auto-pick when no published window spans the seeded check-in). Nights,
    // rounds and the spanned dates are seeded; net cost is left blank for the
    // agent. Group size is seeded onto the package downstream (package.js).
    const prefillCustomPricing = (startMoment) => {
        // Seed the nights/rounds from the enquiry where present
        const seedNights = parseInt(ctxEnquirySeed?.nights)
        const seedRounds = parseInt(ctxEnquirySeed?.rounds)

        if (details.option_type === 'resort' && !isNaN(seedNights)) {
            setCustNights(seedNights)
        }
        if (!isNaN(seedRounds)) {
            setCustRounds(seedRounds)
        }

        // Highlight the spanned dates on the calendar so the entry is anchored to
        // the seeded check-in (uses the resort nights, falling back to a single
        // day for courses which have no nights concept)
        const spanNights = details.option_type === 'resort' && !isNaN(seedNights) ? seedNights : 0
        setChosenDates(calculateIncludedDates(spanNights, startMoment))

        // There's nothing to auto-pick, so make sure the row is open and the room
        // chooser is dismissed - the agent completes the ad-hoc entry manually.
        setAwaitingRoomChoice(false)
        setOpen(true)
    }

    // Save the custom pricing option into the state
    const saveCustomPricing = () => {
        // Get the start of the first included date and convert to a seconds timestamp
        const fromDate = moment(chosenDates[0], 'DD-MM-YYYY').startOf('day').format('X')
        const untilDate = moment(chosenDates[chosenDates.length - 1], 'DD-MM-YYYY').endOf('day').format('X')

        // Generate a unique ID for the package
        const uniqueID = Math.random().toString(36).substr(2, 9)

        // Setup an object to store the details for a custom pricing package
        let newCustomPricing = {
            id: uniqueID,
            price_type: details.option_type,
            resort_id: details.objectID,
            is_draft: false,
            name: 'Custom Pricing',
            type: 'custom',
            is_custom: true,
            base_markup: 20,
            adjusted_price_highest: null,
            price_highest: null,
            adjusted_price_lowest: null,
            price_lowest: null,
            adjusted_markup: null,
            rounds: parseInt(custRounds),
            buggies: [],
            start_date: {
                seconds: fromDate,
                nanoseconds: 0
            },
            end_date: {
                seconds: untilDate,
                nanoseconds: 0
            },
            included_dates: chosenDates,
            from_date: fromDate,
            parent_type: details.option_type,
            parent_title: details.title,
            parent_currency: printCurrencySymbol(details.currency)
        }

        // Are adding a pricing package for a resort?
        if (details.option_type === 'resort') {
            // Create the GUID IDs for the board and room types
            const boardTypeID = Math.random().toString(36).substr(2, 9)
            const roomTypeID = Math.random().toString(36).substr(2, 9)

            // Add the nights, board types and room types to the package
            newCustomPricing.nights = parseInt(custNights)
            newCustomPricing.board_types = [{
                type: custBoardType,
                cost: 0,
                base: true,
                _id: boardTypeID
            }]
            newCustomPricing.room_types = [{
                type: custRoomType,
                cost: parseInt(custNetCost),
                base: true,
                _id: roomTypeID
            }]
        }

        // Are adding a pricing package for a course?
        if (details.option_type === 'course') {
            newCustomPricing.name = `${parseInt(custRounds)} Green Fee${parseInt(custRounds) > 1 ? 's' : ''}`

            // Create a GUID for the fee type ID
            const feeTypeID = Math.random().toString(36).substr(2, 9)

            // Add the fee type to the package
            newCustomPricing.fee_types = [{
                type: custFeeType,
                cost: parseInt(custNetCost),
                base: true,
                _id: feeTypeID
            }]
        }

        // Then set the package in to the global context
        setCtxSelectedPackages((ctxSelectedPackages) => ([...ctxSelectedPackages, {
            ...newCustomPricing
        }]))

        // And clear the state
        setCustNights(null)
        setCustRounds(null)
        setCustRoomType(null)
        setCustBoardType(null)
        setCustFeeType(null)
        setCustNetCost(null)
        setChoosingNights('')
        setChoosingNightsPackage({})
        setChosenDates([])
    }

    // Has a package already been selected for this option? Once it has, the
    // up-front room chooser is no longer relevant.
    const thisOptionSelected = ctxSelectedPackages.some((pkg) => (
        pkg.resort_id === details.objectID || pkg.course_id === details.objectID
    ))

    return (
        <div className='quote-option-row-open'>
            <div className='quote-option-row-open-head' onClick={() => setOpen((open) => !open)}>
                <div className='quote-option-row-title'>
                    {details.option_type === 'resort' ? <Badge label='Resort' type='INFO' /> : ''}
                    {details.option_type === 'course' ? <Badge label='Course' type='POSITIVE' /> : ''}
                    
                    {details.title}
                </div>

                <div className='quote-option-row-toggle-open'>
                    {open ? <CaretUpIcon /> : <CaretDownIcon />}
                </div>
            </div>

            {/* Mismatch sits outside the collapsible body so it stays visible
                even when the picker is collapsed after an auto-pick. */}
            {autoPickMismatch &&
                <p className='auto-pick-mismatch'>
                    <WarningTriangleIcon />
                    {autoPickMismatch}
                </p>
            }

            {/* Room-type-first chooser (seeded build tab). The agent ticks the
                room types that suit the group and we auto-pick a package offering
                one of them - or they can drop to manual date/package selection. */}
            {awaitingRoomChoice && !thisOptionSelected &&
                <div className='build-quote-room-choice'>
                    <p className='build-quote-room-choice-title'>
                        Which room types would suit this group? Pick any that work and the matching package is found for you.
                    </p>

                    <div className='build-quote-room-choice-options'>
                        {Object.entries(roomTypeChoices).map(([key, label]) => (
                            <Checkbox
                                key={key}
                                label={label}
                                checked={selectedRoomTypes.includes(key)}
                                onClick={() => toggleRoomType(key)} />
                        ))}
                    </div>

                    <div className='build-quote-room-choice-actions'>
                        <Button
                            label='Find best package'
                            disabled={selectedRoomTypes.length === 0}
                            onClick={() => confirmRoomChoice()} />

                        <Button
                            type='secondary'
                            label='Pick manually instead'
                            onClick={() => { setAwaitingRoomChoice(false); setOpen(true) }} />
                    </div>
                </div>
            }

            <div className={['quote-option-row-open-body', open ? 'is-open' : ''].join(' ').trim()}>
                <div className='quote-package-calendar'>
                    <CalendarUI
                        clearPackages={() => setWindowPackages([])}
                        dateUpdated={handleDateChosen}
                        priceWindows={details?.price_windows || []}
                        chosenDates={chosenDates}
                        currency={printCurrencySymbol(details.currency)} />
                </div>

                <div className={['quote-available-packages', chosenStartDate ? 'is-visible' : ''].join(' ').trim('')}>
                    {windowPackages.map((windowPackage) => (
                        <div className={['holiday-price-row'].join(' ').trim()}>
                            {windowPackage.id === choosingNightsID &&
                                <div className='quote-choosing-nights'>
                                    <Input
                                        type='number'
                                        placeholder='How many nights:'
                                        value={choosingNights}
                                        onChange={setChoosingNights} />

                                    <Button
                                        label='Save'
                                        onClick={() => completeSelectedPackage()}
                                        disabled={isNaN(parseInt(choosingNights)) || parseInt(choosingNights) < 0 || dateOverrun} />

                                    {dateOverrun && 
                                        <p className='overrun-error'>
                                            <WarningTriangleIcon />
                                            Adding {choosingNights} nights falls outside the current price window.
                                        </p>
                                    }
                                </div>
                            }

                            <div className='price-row-name'>
                                {(windowPackage.price_type === 'resort' && windowPackage.type === 'per_night') ?
                                    <Badge label='Per Night' type='SPECIAL' />
                                 : null }

                                {(windowPackage.price_type === 'resort' && windowPackage.type === 'pre_defined') ?
                                    <Badge label='Pre Defined' type='SPECIAL' />
                                 : null }

                                <p>
                                    {(windowPackage.course_name && windowPackage.course_name.length) > 0 ? 
                                        <>{windowPackage.course_name?.replace(/&#039;/g, "'")} - </> : ''}

                                    {windowPackage.name?.replace(/&#039;/g, "'")}
                                </p>

                                {ctxSelectedPackages.filter((pkg) => pkg.id === windowPackage.id).length > 0 && 
                                    <div className='price-option-action' onClick={()=> removeSelectedPackage(windowPackage)}>
                                        <MinusIcon />
                                    </div>
                                }
                            </div>

                            {ctxSelectedPackages.filter((pkg) => pkg.id === windowPackage.id).length === 0 && 
                                <div className='price-row-details'>
                                    <div className='price-option-dates'>
                                        <p>{moment(windowPackage.start_date.seconds, 'X').format('DD-MMM-YYYY')}</p>
                                        <ArrowRightIcon />
                                        <p>{moment(windowPackage.end_date.seconds, 'X').format('DD-MMM-YYYY')}</p>
                                    </div>

                                    <div className='price-option-range'>
                                        <p>£{windowPackage.price_lowest}pp - £{windowPackage.price_highest}pp</p>
                                    </div>

                                    <div className='price-option-actions'>
                                        <div className='price-option-action' onClick={()=> saveSelectedPackage(windowPackage)}>
                                            <PlusIcon />
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    ))}

                    <div className={['holiday-price-row'].join(' ').trim()}>
                        <div className='price-row-name with-icon'>
                            <WarningTriangleIcon />
                            <p>Enter Pricing Details</p>
                        </div>

                        <div className='price-row-details no-flex'>
                            {windowPackages.length === 0 &&
                                <p className='no-price-window-notice'>
                                    There are no pricing options available from this date, enter the relevant details below to continue onto configuration.
                                </p>
                            }
                            {windowPackages.length > 0 &&
                                <p className='no-price-window-notice'>
                                    Setup an ad-hoc pricing window for by entering the details below.
                                </p>
                            }

                            <div className='no-price-window-options'>
                                {details.option_type === 'resort' &&
                                    <div className='no-price-window-option-row'>
                                        <p>Nights</p>

                                        <Input
                                            type='number'
                                            value={custNights}
                                            onChange={setCustNights}
                                            placeholder='Nights' />
                                    </div>
                                }

                                <div className='no-price-window-option-row'>
                                    <p>Rounds</p>

                                    <Input
                                        type='number'
                                        value={custRounds}
                                        onChange={setCustRounds}
                                        placeholder='Rounds' />
                                </div>

                                {details.option_type === 'resort' &&
                                    <>
                                        <div className='no-price-window-option-row'>
                                            <p>Room type</p>

                                            <Select
                                                selected={custRoomType}
                                                placeholder='Choose room type'
                                                options={ROOM_TYPES}
                                                onSelect={({ option }) => setCustRoomType(option)} />
                                        </div>

                                        <div className='no-price-window-option-row'>
                                            <p>Board type</p>

                                            <Select
                                                selected={custBoardType}
                                                placeholder='Choose board type'
                                                options={BOARD_TYPES}
                                                onSelect={({ option }) => setCustBoardType(option)} />
                                        </div>
                                    </>
                                }

                                {details.option_type === 'course' &&
                                    <div className='no-price-window-option-row'>
                                        <p>Fee type</p>

                                        <Select
                                            selected={custFeeType}
                                            placeholder='Choose fee type'
                                            options={FEE_TYPES}
                                            onSelect={({ option }) => setCustFeeType(option)} />
                                    </div>
                                }

                                <div className='no-price-window-option-row'>
                                    <p>Net Cost</p>

                                    <Input
                                        type='number'
                                        value={custNetCost}
                                        onChange={setCustNetCost}
                                        symbol={printCurrencySymbol(details.currency)}
                                        placeholder='Net cost' />
                                </div>

                                <div className='no-price-window-add'>
                                    <Button
                                        label='Add'
                                        onClick={() => saveCustomPricing()}
                                        disabled={details?.option_type === 'course' ? (isNaN(parseInt(custRounds)) || parseInt(custRounds) < 0 || isNaN(parseInt(custNetCost)) || parseInt(custNetCost) < 0) : (isNaN(parseInt(custNights)) || parseInt(custNights) < 0 || isNaN(parseInt(custNetCost)) || parseInt(custNetCost) < 0)} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}